/**
 * Phase 18 — Admin Document Library + Team-Member Export.
 *
 * HR Admin only. Three tabs:
 *   1. All Documents        — unified, filterable library of every uploaded doc
 *   2. By Team Member       — pick a TM, see a roll-up, download the full file
 *                              as a structured zip (with explicit consent).
 *   3. Export History       — last N team-member exports with sha256 + size.
 *
 * The Team-Member export endpoint is rate-limited to 10/hour per admin on
 * the server. The consent modal here is the user-facing acknowledgement.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import {
  Files,
  Search,
  RefreshCw,
  Download,
  Filter,
  Inbox,
  ShieldAlert,
  Archive,
  User as UserIcon,
  FileText,
  History,
  Hash,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AdminPageHeader from "./AdminPageHeader";

const CATEGORIES = [
  "Investigation Notes",
  "Witness Statements",
  "Employee Statements",
  "Emails",
  "HR Documentation",
  "Other Evidence",
];

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function shortHash(h) {
  if (!h) return "—";
  return `${h.slice(0, 8)}…${h.slice(-6)}`;
}

// Trigger a browser download from an authenticated blob fetch via our axios
// client (so the JWT travels with the request).
async function downloadFromApi(path, filename) {
  const { data, headers } = await api.get(path, { responseType: "blob" });
  const sha = headers["x-content-sha256"];
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 200);
  return { sha };
}

// ---------------------------------------------------------------------------
// Tab 1 — All Documents
// ---------------------------------------------------------------------------

function AllDocumentsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("__all__");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        offset: page * limit,
      };
      if (q.trim()) params.q = q.trim();
      if (category && category !== "__all__") params.category = category;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/admin/documents", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Failed to load documents",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onApplyFilters = (e) => {
    e?.preventDefault?.();
    if (page === 0) fetchRows();
    else setPage(0);
  };

  const onDownload = async (doc) => {
    try {
      await downloadFromApi(
        `/admin/documents/${doc.id}/download`,
        doc.filename || `document_${doc.id}.bin`,
      );
      toast.success(`Downloaded ${doc.filename}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Download failed",
      );
    }
  };

  const hasNext = (page + 1) * limit < total;
  const hasPrev = page > 0;

  return (
    <div data-testid="docs-tab-all" className="space-y-4">
      <form
        onSubmit={onApplyFilters}
        className="brand-card flex flex-wrap items-end gap-3 px-4 py-3"
        data-testid="docs-all-filter-bar"
      >
        <div className="min-w-[200px] flex-1">
          <Label className="mb-1 block text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
            Search
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--page-fg-muted)]" strokeWidth={1.6} />
            <Input
              data-testid="docs-all-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filename, case ID, or team member"
              className="pl-7"
            />
          </div>
        </div>
        <div className="min-w-[180px]">
          <Label className="mb-1 block text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
            Category
          </Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="docs-all-category">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any category</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
            From
          </Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="docs-all-from"
            className="w-[150px]"
          />
        </div>
        <div>
          <Label className="mb-1 block text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
            To
          </Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="docs-all-to"
            className="w-[150px]"
          />
        </div>
        <Button
          type="submit"
          size="sm"
          className="h-9"
          data-testid="docs-all-apply"
        >
          <Filter className="mr-1.5 h-4 w-4" strokeWidth={1.6} />
          Apply
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9"
          data-testid="docs-all-refresh"
          onClick={() => (page === 0 ? fetchRows() : setPage(0))}
        >
          <RefreshCw className="mr-1.5 h-4 w-4" strokeWidth={1.6} />
          Refresh
        </Button>
      </form>

      <div
        className="brand-card overflow-hidden"
        data-testid="docs-all-table-wrap"
      >
        <div className="flex items-center justify-between border-b border-[color:var(--border-subtle)] px-4 py-2 text-xs text-[color:var(--page-fg-muted)]">
          <span data-testid="docs-all-total">
            {loading ? "Loading…" : `${total.toLocaleString()} document${total === 1 ? "" : "s"}`}
          </span>
          <span>
            Page {page + 1} of {Math.max(1, Math.ceil(total / limit))}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table
            className="w-full text-left text-sm"
            data-testid="docs-all-table"
          >
            <thead className="text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
              <tr>
                <th className="px-4 py-2">Filename</th>
                <th className="px-4 py-2">Case</th>
                <th className="px-4 py-2">Team Member</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Uploader</th>
                <th className="px-4 py-2">Uploaded</th>
                <th className="px-4 py-2 text-right">Size</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-[color:var(--page-fg-muted)]">
                      <Inbox className="h-6 w-6" strokeWidth={1.6} />
                      <span data-testid="docs-all-empty">No documents match these filters.</span>
                    </div>
                  </td>
                </tr>
              )}
              {rows.map((d) => (
                <tr
                  key={d.id}
                  className="border-t border-[color:var(--border-subtle)] align-top hover:bg-[color:var(--surface-2)]"
                  data-testid={`docs-all-row-${d.id}`}
                >
                  <td className="max-w-[260px] truncate px-4 py-2 font-medium" style={{ color: "var(--page-fg)" }}>
                    <span className="inline-flex items-center gap-1.5">
                      {d.is_encrypted && (
                        <Lock className="h-3.5 w-3.5 text-[color:var(--page-fg-muted)]" strokeWidth={1.8} title="Encrypted at rest" />
                      )}
                      <span className="truncate">{d.filename || "(unnamed)"}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-[12.5px]">{d.case_native_id || "—"}</td>
                  <td className="px-4 py-2">
                    {d.team_member_name ? (
                      <div className="leading-tight">
                        <div>{d.team_member_name}</div>
                        <div className="text-[11px] text-[color:var(--page-fg-muted)]">
                          #{d.position_id || d.file_number || "—"} · {d.service_line || "—"}
                        </div>
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2 text-[12.5px]">{d.category}</td>
                  <td className="px-4 py-2 text-[12.5px]">{d.uploaded_by_name || "—"}</td>
                  <td className="px-4 py-2 text-[12.5px]">{fmtDateTime(d.uploaded_at)}</td>
                  <td className="px-4 py-2 text-right text-[12.5px] tabular-nums">{fmtBytes(d.size_bytes)}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8"
                      onClick={() => onDownload(d)}
                      data-testid={`docs-all-download-${d.id}`}
                    >
                      <Download className="mr-1 h-3.5 w-3.5" strokeWidth={1.8} />
                      Download
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-[color:var(--border-subtle)] px-4 py-2 text-xs">
          <span className="text-[color:var(--page-fg-muted)]">
            Showing {rows.length > 0 ? page * limit + 1 : 0}–{page * limit + rows.length} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              disabled={!hasPrev || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="docs-all-prev"
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              disabled={!hasNext || loading}
              onClick={() => setPage((p) => p + 1)}
              data-testid="docs-all-next"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — By Team Member (with consent modal)
// ---------------------------------------------------------------------------

function ByTeamMemberTab() {
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [summary, setSummary] = useState(null); // { cases, docs, witnesses }
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fetch team members. Service paginates at 500; the picker shows the most
  // recent N matching the search query (case-insensitive).
  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const { data } = await api.get("/team-members", {
        params: { limit: 500, offset: 0 },
      });
      const items = Array.isArray(data) ? data : data.items || [];
      setMembers(items);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Failed to load team members",
      );
    } finally {
      setLoadingMembers(false);
    }
  };
  useEffect(() => {
    fetchMembers();
  }, []);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members.slice(0, 100);
    return members
      .filter(
        (m) =>
          (m.name || "").toLowerCase().includes(q) ||
          String(m.file_number || "").includes(q) ||
          (m.department || "").toLowerCase().includes(q),
      )
      .slice(0, 100);
  }, [members, search]);

  const selected = useMemo(
    () => members.find((m) => m.id === selectedId) || null,
    [members, selectedId],
  );

  // When selection changes, get a doc-count summary by querying admin/documents
  // filtered to the team_member_id.
  useEffect(() => {
    if (!selectedId) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    setSummaryLoading(true);
    (async () => {
      try {
        const docsRes = await api.get("/admin/documents", {
          params: { team_member_id: selectedId, limit: 1, offset: 0 },
        });
        if (cancelled) return;
        setSummary({
          documents: docsRes.data?.total ?? 0,
          team_member: selected,
        });
      } catch (err) {
        if (!cancelled) {
          setSummary({ documents: 0, team_member: selected });
        }
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, selected]);

  const startExport = () => {
    setConsent1(false);
    setConsent2(false);
    setShowConsent(true);
  };

  const confirmAndDownload = async () => {
    if (!consent1 || !consent2 || !selectedId) return;
    setExporting(true);
    try {
      const tm = selected || {};
      const last = (tm.name || "Unknown").trim().split(/\s+/).slice(-1)[0] || "Unknown";
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const fname = `${last}_${tm.file_number || "0"}_export_${stamp}.zip`;
      const { sha } = await downloadFromApi(
        `/admin/team-members/${selectedId}/export.zip`,
        fname,
      );
      toast.success(
        sha
          ? `Export ready · sha256 ${sha.slice(0, 12)}…`
          : "Export downloaded",
      );
      setShowConsent(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(
          "Rate limit reached — 10 team-member exports per hour. Try again later.",
        );
      } else {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Export failed",
        );
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="docs-tab-by-tm" className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        {/* Picker */}
        <div className="brand-card overflow-hidden">
          <div className="border-b border-[color:var(--border-subtle)] p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--page-fg-muted)]" strokeWidth={1.6} />
              <Input
                placeholder="Search by name, position ID, or department"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="docs-tm-search"
                className="pl-7"
              />
            </div>
            <p className="mt-2 text-[11px] text-[color:var(--page-fg-muted)]">
              {loadingMembers
                ? "Loading…"
                : `${filteredMembers.length} of ${members.length} shown`}
            </p>
          </div>
          <ul
            className="max-h-[440px] overflow-y-auto"
            data-testid="docs-tm-list"
          >
            {filteredMembers.length === 0 && !loadingMembers && (
              <li className="px-4 py-8 text-center text-sm text-[color:var(--page-fg-muted)]">
                No team members yet.
              </li>
            )}
            {filteredMembers.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  data-testid={`docs-tm-pick-${m.id}`}
                  onClick={() => setSelectedId(m.id)}
                  className="flex w-full items-start gap-2 border-b border-[color:var(--border-subtle)] px-3 py-2 text-left text-sm transition-colors hover:bg-[color:var(--surface-2)]"
                  style={{
                    backgroundColor:
                      selectedId === m.id ? "var(--surface-2)" : "transparent",
                  }}
                >
                  <UserIcon
                    className="mt-0.5 h-3.5 w-3.5 flex-shrink-0"
                    strokeWidth={1.8}
                    style={{
                      color:
                        selectedId === m.id
                          ? "#5EAB45"
                          : "var(--page-fg-muted)",
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium" style={{ color: "var(--page-fg)" }}>
                      {m.name}
                    </div>
                    <div className="text-[11px] text-[color:var(--page-fg-muted)]">
                      #{m.position_id || m.file_number} · {m.service_line || "—"}
                      {m.department ? ` · ${m.department}` : ""}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Detail / export */}
        <div className="brand-card p-5" data-testid="docs-tm-detail">
          {!selected && (
            <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-2 text-[color:var(--page-fg-muted)]">
              <Files className="h-6 w-6" strokeWidth={1.6} />
              <p className="text-sm">Select a team member to preview their file.</p>
            </div>
          )}
          {selected && (
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--page-fg-muted)]">
                  Team Member
                </p>
                <h3
                  className="font-serif text-2xl font-semibold"
                  data-testid="docs-tm-name"
                  style={{ color: "var(--page-fg)" }}
                >
                  {selected.name}
                </h3>
                <p className="mt-1 text-sm text-[color:var(--page-fg-secondary)]">
                  Position ID {selected.position_id || selected.file_number} · {selected.service_line || "—"}
                  {selected.department ? ` · ${selected.department}` : ""}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md border border-[color:var(--border-subtle)] p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                    Documents
                  </p>
                  <p
                    className="mt-0.5 font-serif text-2xl font-semibold tabular-nums"
                    data-testid="docs-tm-docs-count"
                    style={{ color: "var(--page-fg)" }}
                  >
                    {summaryLoading ? "—" : summary?.documents ?? 0}
                  </p>
                </div>
                <div className="rounded-md border border-[color:var(--border-subtle)] p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                    Service Line
                  </p>
                  <p className="mt-0.5 font-mono text-base font-medium" style={{ color: "var(--page-fg)" }}>
                    {selected.service_line || "—"}
                  </p>
                </div>
                <div className="rounded-md border border-[color:var(--border-subtle)] p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                    Position ID
                  </p>
                  <p className="mt-0.5 font-mono text-base font-medium" style={{ color: "var(--page-fg)" }}>
                    {selected.position_id || selected.file_number}
                  </p>
                </div>
              </div>

              <div
                className="rounded-md border p-3 text-[12.5px] leading-relaxed"
                style={{
                  backgroundColor: "rgba(94,171,69,0.06)",
                  borderColor: "rgba(94,171,69,0.30)",
                  color: "var(--page-fg-secondary)",
                }}
              >
                <p className="font-semibold" style={{ color: "var(--page-fg)" }}>
                  What's in the export?
                </p>
                <p className="mt-1">
                  A structured <code className="font-mono text-[12px]">.zip</code> containing:
                  a manifest, a one-page case-summary PDF per case, all attached
                  documents (decrypted to plaintext), witness interview transcripts,
                  and a hash-chained audit trail per case.
                </p>
                <p className="mt-1">
                  This action is rate-limited to <strong>10 exports per hour per admin</strong>
                  {" "}and logged in the export history.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={startExport}
                  data-testid="docs-tm-export-btn"
                  className="h-10"
                >
                  <Archive className="mr-1.5 h-4 w-4" strokeWidth={1.8} />
                  Export Team Member File (ZIP)
                </Button>
                <span className="text-[12px] text-[color:var(--page-fg-muted)]">
                  Confirms intent before any download.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Consent modal */}
      <Dialog open={showConsent} onOpenChange={setShowConsent}>
        <DialogContent data-testid="docs-tm-consent-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-[color:var(--warning-fg)]" strokeWidth={1.8} />
              Confirm team-member file export
            </DialogTitle>
            <DialogDescription>
              This will package every case, document, witness interview, and audit row
              for <strong>{selected?.name}</strong> into a downloadable archive.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div
              className="rounded-md border p-3 text-[12.5px] leading-relaxed"
              style={{
                backgroundColor: "var(--warning-bg)",
                borderColor: "var(--warning-border)",
                color: "var(--page-fg)",
              }}
            >
              The archive contains confidential personal information. Anyone with
              the file can read it. Handle per Goodwin Living's data retention and
              disclosure policies, and delete local copies once you no longer need them.
            </div>
            <label className="flex cursor-pointer items-start gap-2 text-sm" data-testid="docs-tm-consent-row-1">
              <Checkbox
                checked={consent1}
                onCheckedChange={(v) => setConsent1(!!v)}
                data-testid="docs-tm-consent-1"
                className="mt-0.5"
              />
              <span>
                I confirm I have a <strong>legitimate business need</strong> for this
                export and am authorised to receive it.
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm" data-testid="docs-tm-consent-row-2">
              <Checkbox
                checked={consent2}
                onCheckedChange={(v) => setConsent2(!!v)}
                data-testid="docs-tm-consent-2"
                className="mt-0.5"
              />
              <span>
                I understand this action is logged in the audit chain and the
                export history with my user ID and IP.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConsent(false)}
              disabled={exporting}
              data-testid="docs-tm-consent-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAndDownload}
              disabled={!consent1 || !consent2 || exporting}
              data-testid="docs-tm-consent-confirm"
            >
              {exporting ? (
                <>
                  <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Preparing…
                </>
              ) : (
                <>
                  <Download className="mr-1.5 h-4 w-4" strokeWidth={1.8} />
                  Confirm & Download
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Export History
// ---------------------------------------------------------------------------

function ExportHistoryTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/exports/recent", {
        params: { limit: 50 },
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Failed to load export history",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  return (
    <div data-testid="docs-tab-history" className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[12.5px] text-[color:var(--page-fg-secondary)]">
          The last 50 team-member exports, newest first. Each entry is a row in
          the hash-chained audit log.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={fetch}
          data-testid="docs-history-refresh"
        >
          <RefreshCw className="mr-1.5 h-4 w-4" strokeWidth={1.6} />
          Refresh
        </Button>
      </div>

      <div className="brand-card overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="w-full text-left text-sm"
            data-testid="docs-history-table"
          >
            <thead className="text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
              <tr>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Team Member</th>
                <th className="px-4 py-2">Cases</th>
                <th className="px-4 py-2">Docs</th>
                <th className="px-4 py-2">Witnesses</th>
                <th className="px-4 py-2">Audit Rows</th>
                <th className="px-4 py-2 text-right">ZIP Size</th>
                <th className="px-4 py-2">SHA256</th>
                <th className="px-4 py-2">Exported by</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-[color:var(--page-fg-muted)]">
                      <History className="h-6 w-6" strokeWidth={1.6} />
                      <span data-testid="docs-history-empty">
                        No team-member exports yet.
                      </span>
                    </div>
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-[color:var(--border-subtle)] hover:bg-[color:var(--surface-2)]"
                  data-testid={`docs-history-row-${r.id}`}
                >
                  <td className="px-4 py-2 text-[12.5px]">{fmtDateTime(r.exported_at)}</td>
                  <td className="px-4 py-2">
                    <div className="leading-tight">
                      <div className="font-medium" style={{ color: "var(--page-fg)" }}>
                        {r.team_member_name || "—"}
                      </div>
                      <div className="text-[11px] text-[color:var(--page-fg-muted)]">
                        #{r.position_id || r.file_number || "—"}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 tabular-nums">{r.case_count ?? 0}</td>
                  <td className="px-4 py-2 tabular-nums">{r.document_count ?? 0}</td>
                  <td className="px-4 py-2 tabular-nums">{r.witness_interview_count ?? 0}</td>
                  <td className="px-4 py-2 tabular-nums">{r.audit_row_count ?? 0}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtBytes(r.zip_size_bytes)}</td>
                  <td className="px-4 py-2 font-mono text-[11px]" title={r.zip_sha256 || ""}>
                    <span className="inline-flex items-center gap-1">
                      <Hash className="h-3 w-3 text-[color:var(--page-fg-muted)]" strokeWidth={1.8} />
                      {shortHash(r.zip_sha256)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-[12.5px]">
                    <div className="leading-tight">
                      <div>{r.exported_by_name || "—"}</div>
                      <div className="text-[11px] text-[color:var(--page-fg-muted)]">
                        {r.exported_by_email || ""}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------

export default function AdminDocuments() {
  const { user } = useAuth();
  const [tab, setTab] = useState("all");

  const isAdmin = user?.role === "hr" && user?.is_admin === true;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/admin" replace />;

  return (
    <div data-testid="admin-documents-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Documents"
        title="Document library"
        description="Search every uploaded document, or assemble a complete team-member file for legal, audit, or transfer purposes."
        rightSlot={
          <span
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
            style={{
              backgroundColor: "rgba(94,171,69,0.10)",
              color: "#3F7E2D",
              border: "1px solid rgba(94,171,69,0.30)",
            }}
          >
            <FileText className="h-3 w-3" />
            HR Admin only
          </span>
        }
      />

      <Tabs value={tab} onValueChange={setTab} data-testid="admin-docs-tabs">
        <TabsList>
          <TabsTrigger value="all" data-testid="docs-tab-trigger-all">
            <Files className="mr-1.5 h-4 w-4" strokeWidth={1.6} />
            All Documents
          </TabsTrigger>
          <TabsTrigger value="by-tm" data-testid="docs-tab-trigger-by-tm">
            <UserIcon className="mr-1.5 h-4 w-4" strokeWidth={1.6} />
            By Team Member
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="docs-tab-trigger-history">
            <History className="mr-1.5 h-4 w-4" strokeWidth={1.6} />
            Export History
          </TabsTrigger>
        </TabsList>
        <TabsContent value="all" className="mt-4">
          <AllDocumentsTab />
        </TabsContent>
        <TabsContent value="by-tm" className="mt-4">
          <ByTeamMemberTab />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <ExportHistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
